import { useEffect, useState, type FormEvent } from 'react';

import { PageChrome } from './PageChrome';

type WithdrawalFormState = {
  billingEmail: string;
  reason: string;
  website: string;
};

type ApiResponse = {
  success?: boolean;
  message?: string;
  error?: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const initialForm: WithdrawalFormState = {
  billingEmail: '',
  reason: '',
  website: ''
};

export function WithdrawalPage() {
  const [form, setForm] = useState<WithdrawalFormState>(initialForm);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const metaDescription = window.document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const originalTitle = window.document.title;
    const originalDescription = metaDescription?.getAttribute('content') ?? '';

    window.document.title = 'Elállás a szerződéstől | Orienta';
    metaDescription?.setAttribute(
      'content',
      'Fogyasztói elállás kezdeményezése az Orienta szolgáltatásra vonatkozó szerződéshez.'
    );

    return () => {
      window.document.title = originalTitle;
      metaDescription?.setAttribute('content', originalDescription);
    };
  }, []);

  function updateField(field: keyof WithdrawalFormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function validate() {
    const billingEmail = form.billingEmail.trim();
    const reason = form.reason.trim();

    if (!billingEmail) return 'A számlázási email cím megadása kötelező.';
    if (!emailPattern.test(billingEmail)) return 'Érvénytelen email cím.';
    if (reason.length > 5000) return 'Az indoklás maximum 5000 karakter lehet.';
    return '';
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch('/api/withdrawal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billingEmail: form.billingEmail.trim(),
          reason: form.reason.trim(),
          website: form.website
        })
      });
      const body = (await response.json()) as ApiResponse;

      if (!response.ok || !body.success) {
        setError(body.error ?? 'Nem sikerült elküldeni. Kérjük, próbálja újra később.');
        return;
      }

      setIsSent(true);
      setForm(initialForm);
    } catch {
      setError('Hálózati hiba. Kérjük, ellenőrizze az internetkapcsolatát.');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <PageChrome>
      <main className="form-page-main">
        <section className="section-inner form-page-layout">
          <div className="form-page-copy">
            <p className="eyebrow">Elállás</p>
            <h1>Elállás a szerződéstől</h1>
            <p>
              Fogyasztóként az alábbi űrlapon is kezdeményezheted az elállási jog gyakorlását, ha az a szerződésre
              alkalmazandó jogszabályok szerint fennáll.
            </p>

            <div className="withdrawal-sample">
              <h2>Elállási nyilatkozat mintája</h2>
              <p>
                Címzett: Extra SWEET Solutions Kereskedelmi és Szolgáltató Korlátolt Felelősségű Társaság, 2013
                Pomáz, Rozmaring utca 5.; e-mail:{' '}
                <a href="mailto:info@orientaplatform.eu">info@orientaplatform.eu</a>
              </p>
              <p>Alulírott kijelentem, hogy elállok az Orienta szolgáltatásra vonatkozó szerződésemtől.</p>
              <ul>
                <li>Megrendelés vagy előfizetés azonosítója</li>
                <li>A megrendelés dátuma</li>
                <li>A fogyasztó neve és címe</li>
                <li>Dátum és aláírás, ha papíron nyújtják be</li>
              </ul>
            </div>
          </div>

          <section className="form-panel" aria-labelledby="withdrawal-form-title">
            {isSent ? (
              <div className="success-panel" role="status">
                <span className="success-mark">
                  <CheckIcon />
                </span>
                <h2 id="withdrawal-form-title">Köszönjük!</h2>
                <p>Az elállási kezdeményezést megkaptuk, és a megadott e-mail cím alapján feldolgozzuk.</p>
                <a href="/" className="secondary-button">
                  Vissza a főoldalra
                </a>
              </div>
            ) : (
              <>
                <h2 id="withdrawal-form-title">Elállás kezdeményezése</h2>
                <form onSubmit={handleSubmit} noValidate>
                  <label>
                    Számlázási email cím
                    <input
                      value={form.billingEmail}
                      onChange={(event) => updateField('billingEmail', event.target.value)}
                      placeholder="szamlazas@email.hu"
                      type="email"
                      autoComplete="billing email"
                      disabled={isSending}
                    />
                  </label>

                  <label>
                    Indoklás
                    <textarea
                      value={form.reason}
                      onChange={(event) => updateField('reason', event.target.value)}
                      placeholder="Nem kötelező"
                      disabled={isSending}
                    />
                  </label>

                  <p className="form-helper">
                    A kezdéshez add meg a számlázási e-mail címet. Ha az adatok egyeznek a nyilvántartásunkkal,
                    ellenőrző e-mailt küldhetünk, vagy további azonosítást kérhetünk.
                  </p>

                  <label className="honeypot-field">
                    Website
                    <input
                      value={form.website}
                      onChange={(event) => updateField('website', event.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>

                  {error ? (
                    <p className="form-error" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <button className="submit-button" type="submit" disabled={isSending}>
                    {isSending ? 'Küldés folyamatban…' : 'Elállás kezdeményezése'}
                    <ArrowIcon />
                  </button>
                </form>
              </>
            )}
          </section>
        </section>
      </main>
    </PageChrome>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h9M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 10.5l3.5 3.5L17 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
